import { create } from 'zustand'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'pc-theme'

function readStored(): ThemeMode {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === 'light' || value === 'dark' || value === 'system') return value
  } catch {
    /* localStorage indisponível */
  }
  return 'light' // padrão atual: claro
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

export function isDarkMode(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && systemPrefersDark())
}

function applyTheme(mode: ThemeMode): void {
  const dark = isDarkMode(mode)
  document.documentElement.classList.toggle('dark', dark)
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? '#081521' : '#064a87')
}

interface ThemeState {
  mode: ThemeMode
  /** `true` se o tema efetivo (resolvendo "system") é escuro. */
  setMode: (mode: ThemeMode) => void
  toggle: () => void
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readStored(),
  setMode: (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* ignore */
    }
    applyTheme(mode)
    set({ mode })
  },
  toggle: () => get().setMode(isDarkMode(get().mode) ? 'light' : 'dark'),
}))

/** Mantém o tema "system" reagindo à mudança do SO. Retorna cleanup. */
export function subscribeSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mql = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    if (useThemeStore.getState().mode === 'system') applyTheme('system')
  }
  mql.addEventListener('change', handler)
  // Garante consistência já na inicialização.
  applyTheme(useThemeStore.getState().mode)
  return () => mql.removeEventListener('change', handler)
}
